<template>
  <div class="date-range-picker">
    <div class="date-row">
      <div class="date-field">
        <label class="field-label" for="drop-off-date">{{ $t('dropoff') }}</label>
        <div class="date-time-group">
          <input
            id="drop-off-date"
            type="date"
            lang="en-AU"
            class="field-input"
            :value="dropOffDatePart"
            :min="minDate"
            @input="onDropOffDate($event.target.value)"
          />
          <select
            class="field-input time-select"
            :value="dropOffTimePart"
            :disabled="!dropOffDatePart"
            @change="onDropOffTime($event.target.value)"
          >
            <option value="" disabled>Select time</option>
            <option v-for="time in dropOffTimeOptions" :key="'drop-' + time" :value="time">
              {{ formatDisplayTime(time) }}
            </option>
          </select>
        </div>
      </div>
      <div class="date-field">
        <label class="field-label" for="pick-up-date">{{ $t('pickup') }}</label>
        <div class="date-time-group">
          <input
            id="pick-up-date"
            type="date"
            lang="en-AU"
            class="field-input"
            :value="pickUpDatePart"
            :min="minPickUpDate"
            @input="onPickUpDate($event.target.value)"
          />
          <select
            class="field-input time-select"
            :value="pickUpTimePart"
            :disabled="!pickUpDatePart"
            @change="onPickUpTime($event.target.value)"
          >
            <option value="" disabled>Select time</option>
            <option v-for="time in pickUpTimeOptions" :key="'pick-' + time" :value="time">
              {{ formatDisplayTime(time) }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <p class="timezone-hint">{{ $t('timezone') }}: <strong>{{ timezone }}</strong></p>
    <p class="timezone-hint" v-if="businessHoursLabel">Business hours: <strong>{{ businessHoursLabel }}</strong></p>
    <p v-if="error" class="field-error">{{ error }}</p>
  </div>
</template>

<script>
export default {
  name: 'DateRangePicker',
  props: {
    storeId: { type: Number, default: null },
    dropOffDate: { type: String, default: '' },
    pickUpDate: { type: String, default: '' },
    timezone: { type: String, default: 'Australia/Sydney' }
  },
  data () {
    return { error: '' }
  },
  watch: {
    storeId: 'validateCurrentRange',
    dropOffDate: 'validateCurrentRange',
    pickUpDate: 'validateCurrentRange'
  },
  computed: {
    minDateTime () {
      const now = new Date()
      const minutes = now.getMinutes()
      const delta = (15 - (minutes % 15)) % 15
      now.setMinutes(minutes + delta, 0, 0)
      return this.toDateTimeLocal(now)
    },
    minDate () {
      return this.minDateTime.slice(0, 10)
    },
    minPickUpDate () {
      return this.dropOffDatePart || this.minDate
    },
    dropOffDatePart () {
      return this.datePart(this.dropOffDate)
    },
    dropOffTimePart () {
      return this.timePart(this.dropOffDate)
    },
    pickUpDatePart () {
      return this.datePart(this.pickUpDate)
    },
    pickUpTimePart () {
      return this.timePart(this.pickUpDate)
    },
    businessHours () {
      const map = {
        1: { open: '06:00', close: '22:00' },
        2: { open: '05:00', close: '23:00' },
        4: { open: '04:00', close: '23:00' }
      }
      return map[Number(this.storeId)] || null
    },
    businessHoursLabel () {
      if (!this.businessHours) return ''
      return `${this.formatDisplayTime(this.businessHours.open)} - ${this.formatDisplayTime(this.businessHours.close)}`
    },
    dropOffTimeOptions () {
      return this.availableTimeOptions(this.dropOffDatePart, this.minDateTime)
    },
    pickUpTimeOptions () {
      return this.availableTimeOptions(this.pickUpDatePart, this.dropOffDate || this.minDateTime)
    }
  },
  methods: {
    toDateTimeLocal (date) {
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    datePart (value) {
      return value ? String(value).slice(0, 10) : ''
    },
    timePart (value) {
      return value ? String(value).slice(11, 16) : ''
    },
    combineDateTime (date, time) {
      if (!date || !time) return ''
      return `${date}T${time}`
    },
    timeToMinutes (time) {
      const parts = String(time || '00:00').split(':')
      return (parseInt(parts[0], 10) * 60) + parseInt(parts[1], 10)
    },
    minutesToTime (minutes) {
      const pad = n => String(n).padStart(2, '0')
      return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
    },
    buildTimeOptions () {
      const hours = this.businessHours || { open: '00:00', close: '23:45' }
      const options = []
      const open = this.timeToMinutes(hours.open)
      const close = this.timeToMinutes(hours.close)

      for (let minute = open; minute <= close; minute += 15) {
        options.push(this.minutesToTime(minute))
      }

      return options
    },
    availableTimeOptions (dateValue, minDateTime) {
      const options = this.buildTimeOptions()
      if (!dateValue || !minDateTime) return options

      const minDate = this.datePart(minDateTime)
      const minTime = this.timePart(minDateTime)
      if (dateValue !== minDate) return options

      return options.filter(time => time >= minTime)
    },
    ensureAvailableTime (dateValue, currentTime, minDateTime) {
      const options = this.availableTimeOptions(dateValue, minDateTime)
      if (currentTime && options.indexOf(currentTime) !== -1) return currentTime
      return options.length ? options[0] : ''
    },
    formatDisplayTime (time) {
      const parts = String(time || '').split(':')
      let hour = parseInt(parts[0], 10)
      const minute = parts[1] || '00'
      const suffix = hour >= 12 ? 'pm' : 'am'
      hour = hour % 12 || 12
      return `${hour}:${minute}${suffix}`
    },
    isWithinBusinessHours (value) {
      if (!value || !this.businessHours) return true
      const time = String(value).slice(11, 16)
      return time >= this.businessHours.open && time <= this.businessHours.close
    },
    validate (dropOff, pickUp) {
      if (dropOff && !this.isWithinBusinessHours(dropOff)) {
        return this.$t('dropoffBusinessHours') + ' ' + this.businessHoursLabel
      }
      if (pickUp && !this.isWithinBusinessHours(pickUp)) {
        return this.$t('pickupBusinessHours') + ' ' + this.businessHoursLabel
      }
      if (dropOff && pickUp && pickUp < dropOff) {
        return this.$t('pickupAfter')
      }
      return ''
    },
    emit (dropOff, pickUp) {
      this.error = this.validate(dropOff, pickUp)
      this.$emit('valid', !this.error)
      if (this.error) {
        return
      }
      this.$emit('change', { drop_off_date: dropOff, pick_up_date: pickUp })
    },
    onDropOffDate (date) {
      const time = this.ensureAvailableTime(date, this.dropOffTimePart, this.minDateTime)
      const dropOff = this.combineDateTime(date, time)
      let pickUp = this.pickUpDate
      if (pickUp && dropOff && pickUp < dropOff) pickUp = dropOff
      this.emit(dropOff, pickUp)
    },
    onDropOffTime (time) {
      const dropOff = this.combineDateTime(this.dropOffDatePart, time)
      let pickUp = this.pickUpDate
      if (pickUp && dropOff && pickUp < dropOff) pickUp = dropOff
      this.emit(dropOff, pickUp)
    },
    onPickUpDate (date) {
      const minDateTime = this.dropOffDate || this.minDateTime
      const time = this.ensureAvailableTime(date, this.pickUpTimePart, minDateTime)
      this.emit(this.dropOffDate, this.combineDateTime(date, time))
    },
    onPickUpTime (time) {
      this.emit(this.dropOffDate, this.combineDateTime(this.pickUpDatePart, time))
    },
    validateCurrentRange () {
      this.error = this.validate(this.dropOffDate, this.pickUpDate)
      this.$emit('valid', !this.error)
    }
  }
}
</script>

<style scoped>
.date-range-picker { margin-bottom: 20px; }
.date-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
.date-field { min-width: 0; }
.date-time-group {
  display: grid;
  grid-template-columns: 1fr 132px;
  gap: 8px;
}
.field-label { display: block; font-size: 13px; font-weight: 600; color: #1e336f; margin-bottom: 6px; }
.field-input {
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 16px;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  background: #fff;
  color: #1e336f;
  outline: none;
  transition: border-color .2s;
}
.time-select { appearance: auto; }
.field-input:focus { border-color: #1e336f; box-shadow: 0 0 0 3px rgba(30,51,111,.12); }
.timezone-hint { margin-top: 8px; font-size: 12px; color: #4b5563; }
.field-error { margin-top: 6px; font-size: 13px; color: #ef4444; }

@media (min-width: 560px) {
  .date-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
}

@media (max-width: 420px) {
  .date-time-group {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/DateRangePicker.vue